/** @jsxImportSource theme-ui @jsxRuntime classic */
import * as Icons from '@tishman/icons';
import React from 'react';

import { Flex, Box, Link, Text, IntrinsicImage, useThemeUI } from '..';

export interface ModalSocialCarouselCardProps {
  id: string;
  caption: string;
  image: string;
  authorScreenName?: string;
  authorImage?: string;
  url?: string;
  creation_timestamp: string;
}

export const ModalSocialCarouselCard = ({
  image,
  caption,
  authorImage,
  authorScreenName,
  creation_timestamp,
  url,
}: ModalSocialCarouselCardProps): React.JSX.Element => {
  const themeUI = useThemeUI();
  const getGatsbyImageData = (
    image: string,
    width: number,
    height: number,
  ) => ({
    layout: 'fixed' as const,
    width: width,
    height: height,
    images: {
      sources: [
        {
          srcSet: image,
          type: 'image/jpeg',
          sizes: `(min-width: ${width}px) ${width}px, 100vw`,
        },
      ],
      fallback: {
        src: image,
        srcSet: image,
        sizes: `(min-width: ${width}px) ${width}px, 100vw`,
      },
    },
    placeholder: {
      fallback: '',
    },
  });

  // main image
  const mainImageGatsbyData = getGatsbyImageData(image, 480, 480);

  // author image
  // note: this image is not coming through here or on production as of Februrary 2024
  const authorImageGatsbyData = authorImage
    ? getGatsbyImageData(authorImage, 55, 55)
    : undefined; // Assuming square dimensions for the author's image for example

  return (
    <Box sx={{ width: '100%' }}>
      <IntrinsicImage
        alt={caption}
        image={mainImageGatsbyData}
        ratio={515 / 350}
      />
      <Box m={[4, null, 6]}>
        <Flex sx={{ alignItems: 'center', mb: 3 }}>
          {authorImageGatsbyData && (
            <IntrinsicImage
              alt={caption}
              image={authorImageGatsbyData}
              imgStyle={{ borderRadius: '50%' }}
              maxWidth={55}
              minWidth={55}
              ratio={1}
              sx={{ mr: 3, borderRadius: '50%', bg: 'backgroundDisabled' }}
            />
          )}

          <Box>
            {authorScreenName && (
              <Text sx={{ fontWeight: 'medium' }} variant="smallP">
                @{authorScreenName}
              </Text>
            )}
            <Text variant="smallP">{creation_timestamp}</Text>
          </Box>
        </Flex>
        <Text mb={5} mt={4}>
          {caption}
        </Text>
        <Flex>
          {url && (
            <Flex sx={{ alignItems: 'center' }}>
              <Link href={url} variant="underline">
                Source
              </Link>
              <Icons.ExternalLinkArrow
                style={{
                  marginLeft: themeUI.theme.space[2],
                  height: 19,
                  width: 19,
                }}
              />
            </Flex>
          )}
        </Flex>
      </Box>
    </Box>
  );
};
